import { useState, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Eye, EyeOff, Zap, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

interface WiFiCardProps {
  networkName: string | null;
  password: string | null;
  darkMode: boolean;
}

export const WiFiCard = memo(function WiFiCard({ networkName, password, darkMode }: WiFiCardProps) {
  const { t } = useTranslation();
  const [copiedNetwork, setCopiedNetwork] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showQR, setShowQR] = useState(false);

  // Generate WiFi QR code string (WIFI:T:WPA;S:networkname;P:password;;)
  const wifiQRString = useMemo(() => {
    if (!networkName) return '';
    const escapedSSID = networkName.replace(/[\\;,":]/g, '\\$&');
    // Open network (no password): T:nopass — a WPA QR with an empty password
    // makes phones fail to join an open network.
    if (!password) return `WIFI:T:nopass;S:${escapedSSID};;`;
    const escapedPass = password.replace(/[\\;,":]/g, '\\$&');
    return `WIFI:T:WPA;S:${escapedSSID};P:${escapedPass};;`;
  }, [networkName, password]);

  if (!networkName) return null;

  const copyToClipboard = async (text: string, type: 'network' | 'password') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'network') {
        setCopiedNetwork(true);
        setTimeout(() => setCopiedNetwork(false), 3000);
      } else {
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 3000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl transition-all ${
        darkMode
          ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-gradient-to-br from-white to-blue-50/50 border border-blue-100 shadow-xl shadow-gray-200/80'
      }`}
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-cyan-500/10 to-blue-500/10 rounded-full blur-xl translate-y-1/2 -translate-x-1/2" />

      <div className="relative p-5">
        {/* Header — strong text title (no icon badge). */}
        <div className={`flex items-center justify-between mb-5 pb-3 border-b ${darkMode ? 'border-slate-700' : 'border-gray-100'}`}>
          <div>
            <h3 className={`font-bold text-lg tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('guest.wifi.title', 'WiFi pristup')}
            </h3>
            <div className="flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-yellow-500" />
              <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                {t('guest.wifi.highSpeed', 'Brzi internet')}
              </span>
            </div>
          </div>
        </div>

        {/* Network Info */}
        <div className="space-y-3">
          {/* Network Name */}
          <div
            className={`group rounded-xl p-4 transition-all duration-300 hover:scale-[1.02] ${
              darkMode
                ? 'bg-slate-700/50 hover:bg-slate-700/70'
                : 'bg-white hover:bg-blue-50/50 shadow-sm'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs font-semibold uppercase tracking-wider ${
                darkMode ? 'text-slate-400' : 'text-gray-400'
              }`}>
                {t('guest.wifi.network', 'Mreža')}
              </span>
              <button
                onClick={() => copyToClipboard(networkName, 'network')}
                aria-label={copiedNetwork ? t('guest.wifi.copied', 'Kopirano') : t('guest.wifi.copyNetwork', 'Kopiraj naziv mreže')}
                className={`p-2.5 rounded-lg transition-all duration-200 ${
                  copiedNetwork
                    ? 'bg-green-500/20 text-green-500 scale-110'
                    : darkMode
                    ? 'hover:bg-slate-600 text-slate-400 hover:text-white'
                    : 'hover:bg-blue-100 text-gray-400 hover:text-blue-600'
                }`}
              >
                {copiedNetwork ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className={`font-mono font-bold text-xl tracking-wide break-all ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {networkName}
            </p>
          </div>

          {/* Password */}
          {password && (
            <div
              className={`group rounded-xl p-4 transition-all duration-300 hover:scale-[1.02] ${
                darkMode
                  ? 'bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20'
                  : 'bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200/50 shadow-sm'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-semibold uppercase tracking-wider ${
                  darkMode ? 'text-blue-300' : 'text-blue-600'
                }`}>
                  {t('guest.wifi.password', 'Lozinka')}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t('guest.wifi.hidePassword', 'Sakrij lozinku') : t('guest.wifi.showPassword', 'Prikaži lozinku')}
                    aria-pressed={showPassword}
                    className={`p-2.5 rounded-lg transition-all duration-200 ${
                      darkMode
                        ? 'hover:bg-slate-600 text-slate-400 hover:text-white'
                        : 'hover:bg-blue-100 text-blue-400 hover:text-blue-600'
                    }`}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => copyToClipboard(password, 'password')}
                    aria-label={copiedPassword ? t('guest.wifi.copied', 'Kopirano') : t('guest.wifi.copyPassword', 'Kopiraj lozinku')}
                    className={`p-2.5 rounded-lg transition-all duration-200 ${
                      copiedPassword
                        ? 'bg-green-500/20 text-green-500 scale-110'
                        : darkMode
                        ? 'hover:bg-slate-600 text-slate-400 hover:text-white'
                        : 'hover:bg-blue-100 text-blue-400 hover:text-blue-600'
                    }`}
                  >
                    {copiedPassword ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              <p className={`font-mono font-bold text-2xl tracking-widest break-all ${
                darkMode ? 'text-cyan-400' : 'text-blue-600'
              }`}>
                {showPassword ? password : '••••••••'}
              </p>
            </div>
          )}
        </div>

        {/* WiFi QR Code */}
        <div className="mt-4">
          <button
            onClick={() => setShowQR(!showQR)}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl transition-all duration-300 ${
              showQR
                ? darkMode
                  ? 'bg-blue-500/20 border border-blue-500/30'
                  : 'bg-blue-50 border border-blue-200'
                : darkMode
                ? 'bg-slate-700/50 hover:bg-slate-700/70'
                : 'bg-gray-50 hover:bg-gray-100'
            }`}
          >
            <QrCode className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <span className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
              {showQR
                ? t('guest.wifi.hideQR', 'Sakrij QR kod')
                : t('guest.wifi.showQR', 'Skeniraj QR za spajanje')
              }
            </span>
          </button>

          {showQR && (
            <div className={`mt-3 flex flex-col items-center gap-2 p-4 rounded-xl ${
              darkMode ? 'bg-white' : 'bg-white border border-gray-200'
            }`}>
              <QRCodeSVG
                value={wifiQRString}
                size={180}
                level="M"
                includeMargin
              />
              <p className="text-xs text-gray-500 text-center">
                {t('guest.wifi.scanToConnect', 'Skenirajte kamerom za automatsko spajanje')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default WiFiCard;
